/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AnimatedCheckCircle } from '@/components/animated-check-circle';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Link } from '@/components/link';
import { SeoMeta } from '@/components/seo-meta';
import { Typography } from '@/components/typography';
import { routes } from '@/route-paths';
import type { PageAnalyticsHandle } from '@/analytics/route-analytics';

const ORDER_NUMBER_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

// RGBARRY-CUSTOMIZATION: keep PDP native Apple Pay page-view reporting identical to the standard
// Storefront Next order-confirmation route.
export const handle = {
    analytics: { routeType: 'order_confirmation' },
} satisfies PageAnalyticsHandle;

/**
 * Confirmation for PDP native Apple Pay orders. The native SFCC completion controller validates
 * the order token before its same-origin iframe is allowed to send this order number to React.
 * No payment data or order bearer credential enters the Storefront Next application.
 */
export default function ApplePayConfirmation() {
    const { orderNo } = useParams();
    const { t } = useTranslation('checkout');
    const validOrderNo = typeof orderNo === 'string' && ORDER_NUMBER_PATTERN.test(orderNo) ? orderNo : null;

    if (!validOrderNo) {
        return (
            <div data-section="apple-pay-confirmation" className="min-h-screen bg-muted/30">
                <SeoMeta title={t('confirmation.orderNotFound')} noIndex />
                <div className="mx-auto max-w-4xl section-container py-10">
                    <Card className="border-muted-foreground/10">
                        <CardContent className="space-y-5 p-8 text-center">
                            <Typography variant="h2" as="h1">
                                {t('confirmation.orderNotFound')}
                            </Typography>
                            <Typography variant="p" className="text-muted-foreground">
                                {t('confirmation.orderNotFoundDescription')}
                            </Typography>
                            <Button asChild>
                                <Link to={routes.home}>{t('confirmation.actions.continueShopping')}</Link>
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div data-section="apple-pay-confirmation" className="min-h-screen bg-muted/30">
            <SeoMeta title={t('confirmation.title')} noIndex />
            <div className="mx-auto max-w-4xl section-container py-10">
                <Card className="border-muted-foreground/10">
                    <CardContent className="space-y-6 p-8 text-center">
                        <div className="flex justify-center">
                            <AnimatedCheckCircle className="size-16" />
                        </div>
                        <div className="space-y-2">
                            <Typography variant="h2" as="h1">
                                {t('confirmation.title')}
                            </Typography>
                            <Typography variant="p" className="text-muted-foreground">
                                {t('confirmation.hero.orderConfirmed')}
                            </Typography>
                            <Typography variant="p" className="font-semibold text-foreground">
                                {t('confirmation.orderNumberLabel', { orderNo: validOrderNo })}
                            </Typography>
                        </div>
                        <Typography variant="p" className="text-muted-foreground">
                            {t('confirmation.hero.emailConfirmation', {
                                email: 'the address provided through Apple Pay',
                            })}
                        </Typography>
                        <Button asChild>
                            <Link to={routes.home}>{t('confirmation.actions.continueShopping')}</Link>
                        </Button>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
